/**
 * RevenueCat Integration Hook
 * Manages RevenueCat SDK initialization and entitlement checking
 */

import { useEffect, useCallback, useRef } from "react";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  fetchUserAccess,
  fetchRevenueCatStatus,
  setRevenueCatLoaded,
  setAccessLoaded,
  updateEntitlements,
  resetSubscription,
} from "@/store/slices/subscriptionSlice";
import {
  UseRevenueCatIntegrationReturn,
  RevenueCatCustomerInfo,
  UserAccessInfo,
} from "@/types/subscription.types";
import { logger } from "@/utils/logger";
import { getCachedSecret } from "@/services/azure/secretsClient";

// Dynamic import to avoid SSR issues
let Purchases: any = null;

export const useRevenueCatIntegration = (): UseRevenueCatIntegrationReturn => {
  const dispatch = useAppDispatch();
  const {
    entitlements,
    status,
    error,
    revenueCatLoaded,
    accessLoaded,
    userAccess,
  } = useAppSelector((state) => state.subscription);

  const { isAuthenticated, user } = useAppSelector((state) => state.auth);
  const initializationRef = useRef(false);

  /**
   * Load RevenueCat SDK dynamically
   */
  const loadRevenueCatSDK = useCallback(async () => {
    if (Purchases) return Purchases;

    try {
      // Dynamic import for RevenueCat SDK
      const { Purchases: PurchasesModule } = await import(
        "@revenuecat/purchases-js"
      );
      Purchases = PurchasesModule;
      return Purchases;
    } catch (error) {
      logger.warn(
        "Failed to load RevenueCat SDK - continuing without RevenueCat:",
        error
      );
      // Don't throw error, just return null to disable RevenueCat
      return null;
    }
  }, []);

  /**
   * Get RevenueCat API key from Azure Key Vault
   */
  const getRevenueCatApiKey = useCallback(async (): Promise<string | null> => {
    try {
      // Use Azure Key Vault secrets following project architecture
      const isProduction = import.meta.env.PROD;
      const secretName = isProduction
        ? "REVENUECAT-API-KEY-PROD"
        : "REVENUECAT-API-KEY-DEV";

      const apiKey = await getCachedSecret(secretName);
      return apiKey;
    } catch (error) {
      logger.warn(
        "[RevenueCat] Failed to fetch API key from Azure Key Vault:",
        error
      );
      return null;
    }
  }, []);

  /**
   * Initialize RevenueCat for authenticated user
   */
  const initializeRevenueCat = useCallback(
    async (userId: string) => {
      if (!isAuthenticated || initializationRef.current) return;

      try {
        logger.log("[RevenueCat] Initializing for user:", userId);

        const RC = await loadRevenueCatSDK();

        if (!RC) {
          logger.warn("[RevenueCat] SDK not available - RevenueCat disabled");
          dispatch(setRevenueCatLoaded(true));
          return;
        }

        const apiKey = await getRevenueCatApiKey();

        if (!apiKey) {
          logger.warn(
            "[RevenueCat] No API key available from Azure Key Vault - RevenueCat disabled"
          );
          dispatch(setRevenueCatLoaded(true));
          return;
        }

        // Configure RevenueCat with explicit logging of user ID
        logger.log("[RevenueCat] Configuring with userId:", {
          value: userId,
          type: typeof userId,
          stringified: String(userId),
        });

        RC.configure({
          apiKey,
          appUserId: userId, // Use learner UUID from backend
        });

        // Get customer info
        const customerInfo: RevenueCatCustomerInfo =
          await RC.getSharedInstance().getCustomerInfo();

        logger.log("[RevenueCat] Customer info loaded:", customerInfo);
        logger.log("[RevenueCat] Full customer info structure:", {
          entitlements: customerInfo.entitlements,
          allEntitlements: Object.keys(customerInfo.entitlements?.all || {}),
          customerId: customerInfo.originalAppUserId,
          allKeys: Object.keys(customerInfo),
        });

        // Helper to serialize entitlements
        const serializeEntitlements = (ents: Record<string, any>) => {
          const serialized: Record<string, any> = {};
          if (!ents) return serialized;
          Object.keys(ents).forEach((key) => {
            const ent = ents[key];
            serialized[key] = {
              ...ent,
              latestPurchaseDate: ent.latestPurchaseDate instanceof Date ? ent.latestPurchaseDate.toISOString() : ent.latestPurchaseDate,
              originalPurchaseDate: ent.originalPurchaseDate instanceof Date ? ent.originalPurchaseDate.toISOString() : ent.originalPurchaseDate,
              expirationDate: ent.expirationDate instanceof Date ? ent.expirationDate.toISOString() : ent.expirationDate,
            };
          });
          return serialized;
        };

        // Update Redux state - map SDK CustomerInfo to our type
        const mappedCustomerInfo: RevenueCatCustomerInfo = {
          entitlements: {
            active: serializeEntitlements(customerInfo.entitlements?.active || {}),
            all: serializeEntitlements(customerInfo.entitlements?.all || {}),
          },
          activeSubscriptions: customerInfo.activeSubscriptions,
          allPurchasedProductIdentifiers: customerInfo.allPurchasedProductIdentifiers,
          latestExpirationDate: customerInfo.latestExpirationDate instanceof Date ? customerInfo.latestExpirationDate.toISOString() : customerInfo.latestExpirationDate,
          firstSeen: customerInfo.firstSeen instanceof Date ? customerInfo.firstSeen.toISOString() : customerInfo.firstSeen,
          originalPurchaseDate: customerInfo.originalPurchaseDate instanceof Date ? customerInfo.originalPurchaseDate.toISOString() : customerInfo.originalPurchaseDate,
          managementURL: customerInfo.managementURL,
          originalAppUserId: customerInfo.originalAppUserId,
        };
        dispatch(fetchRevenueCatStatus(mappedCustomerInfo));
        dispatch(setRevenueCatLoaded(true));

        initializationRef.current = true;
      } catch (error) {
        logger.error("[RevenueCat] Initialization failed:", error);
        dispatch(setRevenueCatLoaded(true));
      }
    },
    [isAuthenticated, loadRevenueCatSDK, getRevenueCatApiKey, dispatch]
  );

  /**
   * Fetch user access info and initialize RevenueCat
   */
  const initializeUserSubscription = useCallback(async () => {
    if (!isAuthenticated || !user?.email) {
      logger.warn(
        "[Subscription] Cannot initialize - user not authenticated or no email"
      );
      return;
    }

    try {
      // Use email as RevenueCat app user ID (RevenueCat syncs Stripe webhooks by email)
      const userId = user.email;

      logger.log("[Subscription] Initializing RevenueCat with email:", {
        userId,
      });

      // Initialize RevenueCat with the email
      await initializeRevenueCat(userId);

      // Mark access as loaded after RevenueCat initialization completes
      dispatch(setAccessLoaded(true));
    } catch (error: any) {
      logger.error(
        "[Subscription] Failed to initialize user subscription:",
        error
      );
      dispatch(setAccessLoaded(true));
    }
  }, [isAuthenticated, user?.id, dispatch, initializeRevenueCat]);

  /**
   * Check if user has premium access
   */
  const hasPremiumAccess = useCallback((): boolean => {
    // If RevenueCat is not loaded, assume no premium access (show paywall)
    if (!revenueCatLoaded) {
      return false;
    }

    logger.log(
      "[RevenueCat] Checking premium access. Entitlements:",
      entitlements
    );

    // Check for 'premium' entitlement
    const premiumEntitlement = entitlements["premium"];
    const hasPremium = premiumEntitlement?.isActive === true;

    logger.log("[RevenueCat] Premium entitlement:", {
      premiumEntitlement,
      hasPremium,
    });

    return hasPremium;
  }, [entitlements, revenueCatLoaded]);

  /**
   * Get subscription status
   */
  const getSubscriptionStatus = useCallback(():
    | "free"
    | "premium"
    | "expired"
    | "loading" => {
    if (status === "loading" || !revenueCatLoaded || !accessLoaded) {
      return "loading";
    }

    if (hasPremiumAccess()) {
      return "premium";
    }

    // Check if any entitlement is expired
    const hasExpiredEntitlement = Object.values(entitlements).some(
      (entitlement: any) => !entitlement.isActive && entitlement.expirationDate
    );

    if (hasExpiredEntitlement) {
      return "expired";
    }

    return "free";
  }, [status, revenueCatLoaded, accessLoaded, hasPremiumAccess, entitlements]);

  /**
   * Refresh entitlements from RevenueCat
   */
  const refreshEntitlements = useCallback(async () => {
    if (!isAuthenticated || !userAccess) return;

    try {
      const RC = await loadRevenueCatSDK();
      const customerInfo: RevenueCatCustomerInfo =
        await RC.getSharedInstance().getCustomerInfo();

      // Update Redux state
      const rawEntitlements = customerInfo.entitlements.active || {};
      
      // Serialize dates to prevent Redux non-serializable value errors
      const entitlements: Record<string, any> = {};
      Object.keys(rawEntitlements).forEach((key) => {
        const ent = rawEntitlements[key] as any;
        entitlements[key] = {
          ...ent,
          latestPurchaseDate: ent.latestPurchaseDate instanceof Date ? ent.latestPurchaseDate.toISOString() : ent.latestPurchaseDate,
          originalPurchaseDate: ent.originalPurchaseDate instanceof Date ? ent.originalPurchaseDate.toISOString() : ent.originalPurchaseDate,
          expirationDate: ent.expirationDate instanceof Date ? ent.expirationDate.toISOString() : ent.expirationDate,
        };
      });
      
      dispatch(updateEntitlements(entitlements));

      logger.log("[RevenueCat] Entitlements refreshed");
    } catch (error) {
      logger.error("[RevenueCat] Failed to refresh entitlements:", error);
    }
  }, [isAuthenticated, userAccess, loadRevenueCatSDK, dispatch]);

  /**
   * Initialize on mount when user is authenticated
   */
  useEffect(() => {
    if (isAuthenticated && !initializationRef.current) {
      initializeUserSubscription();
    }
  }, [isAuthenticated, initializeUserSubscription]);

  /**
   * Reset on logout
   */
  useEffect(() => {
    if (!isAuthenticated) {
      initializationRef.current = false;
      dispatch(resetSubscription());
    }
  }, [isAuthenticated, dispatch]);

  return {
    hasPremiumAccess,
    getSubscriptionStatus,
    refreshEntitlements,
    isLoading: status === "loading",
    error: error || null,
  };
};
